/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { map, sortBy } from 'lodash';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTheme } from '@mui/material/styles';
import SelectField from 'common/forms/SelectField';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import { shipmentMode } from 'enums/common.enum';
import {
  getFulfillingPharmacies,
  updateFulfillingPharmacyShippingDefault,
} from 'store/thunks/pharmacyThunks';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import usStates from 'common/constants/usStateAbbreviation';

const getStateName = (abbr) =>
  usStates.find((s) => s.abbreviation === abbr?.toUpperCase())?.name ?? abbr;

const SHIPPING_MODE_LABELS = {
  [shipmentMode.PRX_SHIPMENT]: 'Send to Shipping',
  [shipmentMode.SHIP_VIA_SHIPSTATION]: 'Ship Via ShipStation',
};

const CHIP_STYLES = {
  [shipmentMode.PRX_SHIPMENT]: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    borderColor: '#66BB6A',
  },
  [shipmentMode.SHIP_VIA_SHIPSTATION]: {
    backgroundColor: '#E3F2FD',
    color: '#1565C0',
    borderColor: '#42A5F5',
  },
};

const ShippingDefaultChip = ({ routeTo }) => {
  const label = SHIPPING_MODE_LABELS[routeTo] ?? routeTo ?? 'Not configured';
  const chipSx = CHIP_STYLES[routeTo] ?? {
    backgroundColor: '#F5F5F5',
    color: '#616161',
    borderColor: '#BDBDBD',
  };

  return (
    <Chip
      label={label}
      size='small'
      variant='outlined'
      sx={{
        borderRadius: 1,
        fontWeight: 500,
        fontSize: '0.75rem',
        ...chipSx,
      }}
    />
  );
};

ShippingDefaultChip.propTypes = {
  routeTo: PropTypes.string,
};

ShippingDefaultChip.defaultProps = {
  routeTo: null,
};

const FulfillingPharmacySettings = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { currentTenantId } = useSelector(({ auth }) => auth);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId] ?? []);
  // The pharmacy store holds them in API order; the cards list them A–Z by name.
  const sortedPharmacies = useMemo(
    () => sortBy(fulfillingPharmacies, (pharmacy) => String(pharmacy?.name ?? '').toLowerCase()),
    [fulfillingPharmacies]
  );

  const [editingPharmacy, setEditingPharmacy] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    dispatch(getFulfillingPharmacies());
  }, [dispatch]);

  const handleEditClick = (pharmacy) => {
    setEditingPharmacy(pharmacy);
  };

  const handleFormSubmit = (values) => {
    setPendingChange({
      fulfillingPharmacyId: editingPharmacy.fulfillingPharmacyId,
      routeTo: values.routeTo,
    });
    setEditingPharmacy(null);
    setShowConfirmDialog(true);
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    dispatch(updateFulfillingPharmacyShippingDefault(pendingChange)).then((result) => {
      setIsSubmitting(false);
      setShowConfirmDialog(false);
      if (result) {
        enqueueSnackbar('Facility shipping default updated successfully', { variant: 'success' });
        setShowRefreshDialog(true);
      } else {
        enqueueSnackbar('Failed to update shipping default', { variant: 'error' });
      }
    });
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setPendingChange(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 1 }}>
        <LocalShippingIcon color='primary' />
        <Typography variant='h5' fontWeight={600}>
          Facility Shipping Defaults
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Configure the default shipping service per facility. This determines which shipping workflow
        pharmacists see by default when completing prescription verification.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={2}>
        {map(sortedPharmacies, (pharmacy) => (
          <Grid item xs={12} sm={6} key={pharmacy.fulfillingPharmacyId}>
            <Card
              sx={{
                transition: 'box-shadow 0.2s ease-in-out',
                boxShadow:
                  hoveredCard === pharmacy.fulfillingPharmacyId
                    ? theme.shadows[4]
                    : theme.shadows[1],
              }}
              onMouseEnter={() => setHoveredCard(pharmacy.fulfillingPharmacyId)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <CardContent>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                  <Stack direction='row' alignItems='center' gap={1.5}>
                    <FacilityAvatar
                      rxPrefix={pharmacy.config?.rxPrefix ?? pharmacy.name?.charAt(0) ?? ''}
                      facilityName={pharmacy.name}
                      facilityState={pharmacy.state ?? ''}
                    />
                    <Box>
                      <Typography variant='subtitle2' fontWeight={600}>
                        {pharmacy.name}
                      </Typography>
                      {pharmacy.state && (
                        <Typography variant='caption' color='text.secondary'>
                          {getStateName(pharmacy.state)}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack direction='row' alignItems='center' gap={1}>
                    <ShippingDefaultChip
                      routeTo={pharmacy.shipmentConfig?.shipmentRoutes?.routeTo}
                    />
                    <Tooltip title='Edit facility shipping default'>
                      <IconButton size='small' onClick={() => handleEditClick(pharmacy)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={!!editingPharmacy}
        onClose={() => setEditingPharmacy(null)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Edit Facility Shipping Default</DialogTitle>
        <Form
          onSubmit={handleFormSubmit}
          initialValues={{
            routeTo:
              editingPharmacy?.shipmentConfig?.shipmentRoutes?.routeTo ?? shipmentMode.PRX_SHIPMENT,
          }}
          render={({ handleSubmit }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box sx={{ bgcolor: '#EEF1F8', px: 3, py: 2 }}>
                <Stack direction='row' alignItems='center' gap={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      width: 40,
                      height: 40,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {editingPharmacy?.config?.rxPrefix ??
                      editingPharmacy?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant='subtitle1' fontWeight={600} color='primary.main'>
                      {editingPharmacy?.name}
                    </Typography>
                    {editingPharmacy?.state && (
                      <Typography variant='caption' color='text.secondary' fontWeight={600}>
                        {getStateName(editingPharmacy?.state)}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
              <DialogContent>
                <SelectField
                  id='shippingDefault-dropdown'
                  label='Default Shipping Service'
                  name='routeTo'
                >
                  <MenuItem value={shipmentMode.PRX_SHIPMENT}>Send to Shipping</MenuItem>
                  <MenuItem value={shipmentMode.SHIP_VIA_SHIPSTATION}>
                    Ship Via ShipStation
                  </MenuItem>
                </SelectField>
              </DialogContent>
              <DialogActions>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={() => setEditingPharmacy(null)}
                >
                  Cancel
                </Button>
                <Button variant='contained' type='submit'>
                  Save
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelConfirm} fullWidth maxWidth='sm'>
        <DialogTitle>Confirm Facility Shipping Service Change</DialogTitle>
        <DialogContent>
          <Stack direction='row' alignItems='flex-start' gap={1}>
            <WarningAmberIcon color='warning' sx={{ mt: 0.25, flexShrink: 0 }} />
            <Typography variant='body2'>
              Are you sure you want to change the default shipping service? This will change the
              default option pharmacists see when they finish verifying a prescription.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={handleCancelConfirm}>
            Cancel
          </Button>
          <LoadingButton loading={isSubmitting} variant='contained' onClick={handleConfirm}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Post-save refresh notice */}
      <Dialog
        open={showRefreshDialog}
        onClose={() => setShowRefreshDialog(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Facility Shipping Default Updated</DialogTitle>
        <DialogContent>
          <Stack direction='row' alignItems='flex-start' gap={1}>
            <CheckCircleOutlineIcon color='success' sx={{ mt: 0.25, flexShrink: 0 }} />
            <Typography variant='body2'>
              The default shipping service has been updated successfully. POM users currently using
              the application may need to refresh their browser to see the updated default shipping
              behavior.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setShowRefreshDialog(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FulfillingPharmacySettings;
